import IndigoApi from 'communication/IndigoApi';
import LayoutService from 'layout/LayoutService';
import LoginService from 'login/LoginService';
import StreamDataService from 'streamsState/StreamDataService';
import CurrencyService from 'app/currencies/CurrencyService';
import ContactSupportService from 'app/contactSupport/ContactSupportService';
import RequestProgressService from 'requestProgress/RequestProgressService';
import ClickstreamService from 'clickstream/ClickstreamService';
import Services, { DataServices } from 'store/Services';
import { bootstrapWidgetsService } from 'widgets/bootstrapper';
import { PortfolioService } from 'portfolio/PortfolioService';
import { OrdersMessageBufferService } from 'app/store/orders/OrdersMessageBuffer/OrdersMessageBufferService';
import ShareUsersService from './app/ShareUsersService';
import { PortfolioEventService } from 'portfolio/PortfolioEventService';
import { PortfolioSummaryDataService } from 'widgets/portfolioSummary/store/data/PortfolioSummaryDataService';
import { NetworkService } from 'app/NetworkService';
import { CurrentLayoutPersistenceService } from 'layout/CurrentLayoutPersistenceService';
import { PositionEntryBufferService } from 'positionEntry/PositionEntryBufferService';
import { LogMessagesStoreService } from 'logging/store/LogMessagesStoreService';
import { WidgetStreamingService } from 'widgets/streaming/WidgetStreamingService';
import { EmbeddedApiService } from 'embedded/api/EmbeddedApiService';
import { OrdersStreamingService } from 'app/store/orders/OrdersStreamingService';
import { IndigoStore } from 'store/store';
import {
    ActionsDeferredDispatcherFactory,
    ActionsIntervalDispatcherFactory,
} from 'store/ActionsDispatcher';
import IndigoServiceClient from 'communication/IndigoServiceClient';
import { SymbolsApi } from 'symbols/api/SymbolsApi';
import { SymbolsService } from 'symbols/SymbolsService';
import { MyPortfolioCapsuleService } from 'widgets/myPortfolioCapsules/MyPortfolioCapsuleService';
import { MyPortfolioCapsulesDataService } from 'widgets/myPortfolioCapsules/store/data/MyPortfolioCapsulesDataService';
import { MemoryMonitoringService } from 'performance/MemoryMonitoringService';
import { NotificationService } from 'notification/NotificationService';
import { createLoggingService } from 'logging/createLoggingService';
import { SupportVisualisationPopupService } from 'widgets/supportVisualisation/SupportVisualisationPopup/SupportVisualisationPopupService';
import { WidgetMarketDataRequestManager } from 'marketData/WidgetMarketDataRequestManager';
import { MarketDataService } from 'marketData/MarketDataService';
import { OrderValidationApi } from 'orderValidation/OrderValidationApi';
import { UserPreferencesService } from 'userPreferences/UserPreferencesService';
import { UserPreferencesApi } from 'userPreferences/UserPreferencesApi';
import { AvailabilityStatusService } from 'app/AvailabilityStatusService';
import { TradeDatesService } from 'tradeDates/api/TradeDatesService.ts';
import { TradeDatesApi } from 'tradeDates/api/TradeDatesApi.ts';
import { ImpactModelService } from 'impactModel/ImpactModelService.ts';
import { DarkVenueMappingsService } from 'widgets/darkVenueHeatmap/venueMappings/DarkVenueMappingsService.tsx';
import { DarkVenueMappingsApi } from 'widgets/darkVenueHeatmap/venueMappings/DarkVenueMappingsApi.ts';

export class ServiceBootstrapper {
    public services: Services;

    public constructor(indigoApi?: IndigoApi) {
        const logMessagesStore = new LogMessagesStoreService();
        const loggingService = createLoggingService(logMessagesStore);

        const indigoServiceClient = new IndigoServiceClient({
            loggingService,
        });

        const notificationService = new NotificationService();

        if (!indigoApi) {
            indigoApi = new IndigoApi({
                loggingService,
                indigoServiceClient,
                notificationService,
            });
        }

        const actionsDeferredDispatcherFactory =
            new ActionsDeferredDispatcherFactory();
        const actionsIntervalDispatcherFactory =
            new ActionsIntervalDispatcherFactory();

        const requestProgressService = new RequestProgressService();
        const streamDataService = new StreamDataService({
            indigoApi,
            loggingService,
        });

        const currencyService = new CurrencyService({
            indigoApi,
            requestProgressService,
        });

        const userPreferencesService = new UserPreferencesService({
            loggingService,
            requestProgressService,
            api: new UserPreferencesApi({
                loggingService,
                indigoServiceClient,
            }),
        });

        const layoutService = new LayoutService({
            indigoApi,
            requestProgressService,
            loggingService,
            userPreferencesService,
        });

        const symbolValidationApi = new SymbolsApi({
            indigoServiceClient,
            loggingService,
        });

        const symbolsService = new SymbolsService({
            requestProgressService,
            api: symbolValidationApi,
            loggingService,
            userPreferencesService,
        });

        const tradeDatesApi = new TradeDatesApi({
            loggingService,
            indigoServiceClient,
        });
        const tradeDatesService = new TradeDatesService({
            tradeDatesApi,
            requestProgressService,
            loggingService,
        });

        const widgetsService = bootstrapWidgetsService(symbolsService);

        const currentLayoutPersistenceService =
            new CurrentLayoutPersistenceService({
                loggingService,
                layoutService,
                widgetsService,
            });

        const loginService = new LoginService({
            indigoApi,
            requestProgressService,
        });

        const portfolioService = new PortfolioService({ widgetsService });
        const portfolioEventService = new PortfolioEventService(loggingService);
        const orderMessagesBufferService = new OrdersMessageBufferService(
            loggingService
        );

        const shareUsersService = new ShareUsersService({
            indigoApi,
            requestProgressService,
        });

        const portfolioSummaryDataService = new PortfolioSummaryDataService({
            loggingService,
            portfolioService,
            portfolioEventService,
        });

        const embeddedApiService = new EmbeddedApiService({ loggingService });

        const networkService = new NetworkService({
            indigoApi,
            requestProgressService,
            loggingService,
        });

        const availabilityStatusService = new AvailabilityStatusService({
            indigoApi,
            loggingService,
        });

        const darkVenueMappingsApi: DarkVenueMappingsApi =
            new DarkVenueMappingsApi({ indigoServiceClient });
        const darkVenueMappingsService = new DarkVenueMappingsService({
            loggingService,
            requestProgressService,
            api: darkVenueMappingsApi,
        });

        const positionEntryBufferService = new PositionEntryBufferService();

        const marketDataService = new MarketDataService();

        const marketDataRequestManager = new WidgetMarketDataRequestManager({
            loggingService,
            marketDataService,
            symbolsService,
        });

        const dataServices: DataServices = {
            currencyService,
            portfolioService,
            userPreferencesService,
            symbolsService,
        };

        const widgetStreamingService = new WidgetStreamingService({
            indigoApi,
            loggingService,
            portfolioEventService,
            portfolioService,
            widgetsService,
            requestProgressService,
            streamDataService,
            indigoServiceClient,
            marketDataRequestManager,
            dataServices,
        });

        const ordersStreamingService = new OrdersStreamingService({
            loggingService,
            orderMessagesBufferService,
            streamDataService,
            widgetsService,
            portfolioService,
        });

        const orderValidationApi = new OrderValidationApi({
            indigoServiceClient,
            loggingService,
        });

        const myPortfolioCapsuleService = new MyPortfolioCapsuleService({
            widgetsService,
        });

        const myPortfolioCapsulesDataService =
            new MyPortfolioCapsulesDataService({
                loggingService,
                portfolioService,
                portfolioEventService,
            });

        const memoryMonitoring = new MemoryMonitoringService(loggingService);

        const clickstreamService = new ClickstreamService({
            indigoApi,
            requestProgressService,
            memoryMonitoring,
        });

        const contactSupportService = new ContactSupportService({
            indigoApi,
            requestProgressService,
            portfolioService,
            logMessagesStoreService: logMessagesStore,
            indigoServiceClient,
            memoryMonitoring,
            embeddedApiService,
            widgetStreamingService,
        });

        const supportVisualisationPopup = new SupportVisualisationPopupService({
            notificationService,
            loggingService,
            contactSupportService,
        });

        const impactModelService = new ImpactModelService({
            loggingService,
            portfolioService,
            portfolioEventService,
            streamDataService,
        });

        const reset = () => {
            loggingService.info('Services', 'Resetting services');
            widgetStreamingService.resetState();
        };

        this.services = {
            actionsDeferredDispatcherFactory,
            actionsIntervalDispatcherFactory,
            availabilityStatusService,
            clickstreamService,
            contactSupportService,
            currencyService,
            currentLayoutPersistenceService,
            darkVenueMappingsService,
            embeddedApiService,
            impactModelService,
            indigoServiceClient,
            layoutService,
            loggingService,
            logMessagesStore,
            loginService,
            marketDataService,
            marketDataRequestManager,
            memoryMonitoring,
            notificationService,
            orderMessagesBufferService,
            ordersStreamingService,
            requestProgressService,
            streamDataService,
            tradeDatesService,
            indigoApi,
            myPortfolioCapsulesDataService,
            myPortfolioCapsuleService,
            networkService,
            orderValidationApi,
            positionEntryBufferService,
            portfolioService,
            portfolioEventService,
            portfolioSummaryDataService,
            userPreferencesService,
            shareUsersService,
            supportVisualisationPopup,
            symbolsService: symbolsService,
            widgetsService,
            widgetStreamingService,
            reset,
        };
    }

    public applyStore(store: IndigoStore): void {
        this.services.actionsDeferredDispatcherFactory.store = store;
        this.services.actionsIntervalDispatcherFactory.store = store;
        this.services.availabilityStatusService.store = store;
        this.services.contactSupportService.store = store;
        this.services.embeddedApiService.setStore(store);
        this.services.darkVenueMappingsService.store = store;
        this.services.impactModelService.store = store;
        this.services.marketDataService.setStore(store);
        this.services.marketDataRequestManager.setStore(store);
        this.services.myPortfolioCapsulesDataService.store = store;
        this.services.myPortfolioCapsuleService.store = store;
        this.services.networkService.store = store;
        this.services.notificationService.store = store;
        this.services.portfolioService.store = store;
        this.services.portfolioSummaryDataService.store = store;
        this.services.streamDataService.store = store;
        this.services.tradeDatesService.store = store;
        this.services.requestProgressService.store = store;
        this.services.orderMessagesBufferService.store = store;
        this.services.ordersStreamingService.store = store;
        this.services.widgetsService.store = store;
        this.services.widgetStreamingService.store = store;
        this.services.currentLayoutPersistenceService.store = store;
        this.services.positionEntryBufferService.store = store;
        this.services.supportVisualisationPopup.setStore(store);
        this.services.symbolsService.store = store;
        this.services.userPreferencesService.store = store;
    }
}
